import { useTranslation } from "react-i18next";
import { Edit2 } from "iconsax-react";
import { ModalForm, Button, FileViewer } from "@/shared/ui";
import { useGetRegionsQuery } from "@/shared/api/common";
import { getFamilyStatusLabel, type Locale } from "@/shared/utils";
import type { PersonalSheet } from "../../types/personalSheet";

interface ConfirmChangesModalProps {
  changes: Record<string, Record<string, unknown> | unknown[]>;
  files: Record<string, File>;
  personalSheet: PersonalSheet;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  isChiefDirectEdit?: boolean;
}

const FIELD_LABEL_MAP: Record<string, string> = {
  "general.surname": "personalSheet.fields.surname",
  "general.name": "personalSheet.fields.name",
  "general.father_name": "personalSheet.fields.fatherName",
  "general.family_status": "personalSheet.fields.familyStatus",
  "general.citizenship": "personalSheet.fields.citizenship",
  "general.city_of_birth_id": "personalSheet.fields.cityOfBirth",
  "general.city_of_birth_other": "personalSheet.fields.cityOfBirth",
  "contacts.phone": "personalSheet.fields.phone",
  "contacts.phone_additional": "personalSheet.fields.phoneAdditional",
  "contacts.email": "personalSheet.fields.email",
  "addresses.address_registration": "personalSheet.fields.addressRegistration",
  "addresses.address_factual": "personalSheet.fields.addressFactual",
  "id_documents.national_id_number": "personalSheet.fields.nationalIdNumber",
  "id_documents.national_id_issue_date": "personalSheet.fields.nationalIdIssueDate",
  "id_documents.national_id_expiry_date": "personalSheet.fields.nationalIdExpiryDate",
  "id_documents.national_id_issued_by": "personalSheet.fields.nationalIdIssuedBy",
  "banking.bank_name": "personalSheet.fields.bankName",
  "banking.iban_account": "personalSheet.fields.ibanAccount",
  "banking.bik_number": "personalSheet.fields.bikNumber",
  "general.is_student": "personalSheet.fields.isStudent",
  "general.enrollment_start_date": "personalSheet.fields.enrollmentStartDate",
  "general.enrollment_end_date": "personalSheet.fields.enrollmentEndDate",
  "general.has_no_education": "personalSheet.hasNoEducation",
  "general.has_no_work_experience": "personalSheet.hasNoExperience",
  "general.has_no_scientific_works": "personalSheet.hasNoScientificWorks",
  "general.has_no_government_awards": "personalSheet.hasNoGovernmentAwards",
  "general.has_no_social_categories": "personalSheet.hasNoSocialCategories",
  "emergency_contacts.contact_name": "personalSheet.fields.contactName",
  "emergency_contacts.contact_phone": "personalSheet.fields.contactPhone",
  "emergency_contacts.contact_relation": "personalSheet.fields.contactRelation",
  "education.degree": "personalSheet.fields.degree",
  "education.university_name": "personalSheet.fields.universityName",
  "education.specialty": "personalSheet.fields.specialty",
  "education.admission_year": "personalSheet.fields.admissionYear",
  "education.graduation_year": "personalSheet.fields.graduationYear",
  "education.diploma_number": "personalSheet.fields.diplomaNumber",
  "education.is_incomplete": "personalSheet.fields.incompleteEducation",
  "education.left_from_course": "personalSheet.fields.leftFromCourse",
  "experience.organization_name": "personalSheet.fields.organizationName",
  "experience.speciality": "personalSheet.fields.specialty",
  "experience.work_position": "personalSheet.fields.workPosition",
  "experience.work_format": "personalSheet.fields.workFormat",
  "experience.work_address": "personalSheet.fields.workAddress",
  "experience.beginning_date": "personalSheet.fields.beginningDate",
  "experience.end_date": "personalSheet.fields.endDate",
  "social_categories.category_type": "personalSheet.fields.categoryType",
  "social_categories.issue_date": "personalSheet.fields.issueDate",
  "social_categories.expiry_date": "personalSheet.fields.expiryDate",
  "social_categories.notes": "personalSheet.fields.notes",
};

const SECTION_LABEL_MAP: Record<string, string> = {
  general: "personalSheet.sections.general",
  contacts: "personalSheet.sections.contacts",
  addresses: "personalSheet.sections.addresses",
  emergency_contacts: "personalSheet.sections.emergency",
  id_documents: "personalSheet.sections.idDocuments",
  banking: "personalSheet.sections.banking",
  education: "personalSheet.sections.education",
  experience: "personalSheet.sections.experience",
  social_categories: "personalSheet.sections.socialCategories",
  kyc_documents: "personalSheet.sections.general",
};

const FILE_LABEL_MAP: Record<string, string> = {
  national_id_file: "personalSheet.fields.nationalIdFile",
  military_certificate_file: "personalSheet.fields.militaryCertificate",
  worker_photo_file: "personalSheet.fields.photo",
  enrollment_verification_file: "personalSheet.fields.enrollmentVerificationFile",
  bank_certificate_file: "personalSheet.fields.bankCertificateFile",
  diploma_file: "personalSheet.fields.diplomaFile",
  diploma_transcript_file: "personalSheet.fields.diplomaTranscriptFile",
  document_file: "personalSheet.fields.documentFile",
};

export default function ConfirmChangesModal({
  changes,
  files,
  personalSheet,
  onClose,
  onConfirm,
  isSubmitting,
  isChiefDirectEdit = false,
}: ConfirmChangesModalProps) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const { t: tCommon } = useTranslation("Common");
  const locale = (i18n.language as Locale) || "ru";
  const { data: regions = [] } = useGetRegionsQuery();

  function resolveRegionName(id: unknown): string {
    if (typeof id !== "number") return String(id);
    const region = regions.find((r) => r.id === id);
    if (!region) return String(id);
    return locale === "kk" ? region.name_kk : region.name_ru;
  }

  function formatValue(sectionName: string, fieldName: string, value: unknown): string {
    if (value === null || value === undefined) return "—";
    if (typeof value === "boolean") {
      return value ? t("personalSheet.booleanValues.yes") : t("personalSheet.booleanValues.no");
    }
    if (sectionName === "general" && fieldName === "family_status" && typeof value === "string") {
      return getFamilyStatusLabel(value, personalSheet.general.gender, tCommon);
    }
    if (sectionName === "general" && fieldName === "gender" && typeof value === "string") {
      return t(`personalSheet.genderValues.${value}`);
    }
    if (sectionName === "general" && fieldName === "city_of_birth_id") {
      return resolveRegionName(value);
    }
    if (fieldName === "contact_relation" && typeof value === "string") {
      return t(`personalSheet.relationValues.${value}`) || String(value);
    }
    if (fieldName === "degree" && typeof value === "string") {
      return t(`personalSheet.degreeValues.${value}`) || String(value);
    }
    if (fieldName === "work_format" && typeof value === "string") {
      return t(`personalSheet.workFormatValues.${value}`) || String(value);
    }
    if (fieldName === "category_type" && typeof value === "string") {
      return t(`personalSheet.socialCategoryValues.${value}`) || String(value);
    }
    return String(value);
  }

  function getCurrentValue(sectionName: string, fieldName: string): string {
    const section = personalSheet[sectionName as keyof PersonalSheet];
    if (!section || typeof section !== "object" || Array.isArray(section)) return "—";
    if (sectionName === "general" && fieldName === "city_of_birth_id") {
      const cityObj = personalSheet.general.city_of_birth;
      if (!cityObj) return "—";
      return locale === "kk" ? cityObj.name_kk : cityObj.name_ru;
    }
    const raw = (section as unknown as Record<string, unknown>)[fieldName];
    return formatValue(sectionName, fieldName, raw);
  }

  function getFieldLabel(sectionName: string, fieldName: string): string {
    const key = `${sectionName}.${fieldName}`;
    const i18nKey = FIELD_LABEL_MAP[key];
    return i18nKey ? t(i18nKey) : fieldName;
  }

  function getOriginalEntries(sectionName: string): Record<string, unknown>[] {
    const section = personalSheet[sectionName as keyof PersonalSheet];
    return Array.isArray(section) ? (section as unknown as Record<string, unknown>[]) : [];
  }

  function isUnchangedEntry(entry: Record<string, unknown>, original: Record<string, unknown>[]): boolean {
    return original.some((orig) =>
      Object.entries(entry).every(([key, value]) => String(orig[key] ?? "") === String(value ?? "")),
    );
  }

  function renderArraySection(sectionName: string, entries: unknown[]) {
    const original = getOriginalEntries(sectionName);
    const changedEntries = entries.filter(
      (entry) =>
        typeof entry === "object" &&
        entry !== null &&
        !isUnchangedEntry(entry as Record<string, unknown>, original),
    );
    if (changedEntries.length === 0) return null;
    return (
      <div className="flex flex-col gap-2">
        <p className="text-body-regular-sm content-base-secondary">{t("personalSheet.confirm.updatedEntries", { count: changedEntries.length })}</p>
        {changedEntries.map((entry, index) => {
          const record = entry as Record<string, unknown>;
          const visibleFields = Object.entries(record).filter(([k]) => k !== "id");
          return (
            <div key={index} className="flex flex-col gap-1 p-3 surface-secondary-fill radius-xs">
              <p className="text-body-bold-sm content-base-primary">{t("personalSheet.confirm.entryNumber", { number: index + 1 })}</p>
              {visibleFields.map(([fieldName, value]) => (
                <div key={fieldName} className="flex gap-2">
                  <span className="text-body-regular-sm content-base-secondary w-40 shrink-0">{getFieldLabel(sectionName, fieldName)}</span>
                  <span className="text-body-regular-sm content-base-primary">{formatValue(sectionName, fieldName, value)}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  function renderObjectSection(sectionName: string, sectionChanges: Record<string, unknown>) {
    const fields = Object.entries(sectionChanges);
    if (fields.length === 0) return null;
    return (
      <div className="flex flex-col">
        <div className="grid grid-cols-3 gap-x-4 py-2 border-b surface-base-stroke">
          <span className="text-label-sm content-base-secondary">{t("personalSheet.confirm.columnField")}</span>
          <span className="text-label-sm content-base-secondary">{t("personalSheet.confirm.columnCurrent")}</span>
          <span className="text-label-sm content-base-secondary">{t("personalSheet.confirm.columnNew")}</span>
        </div>
        {fields.map(([fieldName, newValue]) => (
          <div key={fieldName} className="grid grid-cols-3 gap-x-4 py-2.5 border-b surface-base-stroke last:border-b-0">
            <span className="text-body-regular-sm content-base-secondary">{getFieldLabel(sectionName, fieldName)}</span>
            <span className="text-body-regular-sm content-base-secondary">{getCurrentValue(sectionName, fieldName)}</span>
            <span className="text-body-bold-sm content-base-primary">{formatValue(sectionName, fieldName, newValue)}</span>
          </div>
        ))}
      </div>
    );
  }

  function renderFooter() {
    return (
      <div className="flex items-center justify-end gap-3">
        <Button variant="secondary" onClick={onClose}>
          {t("personalSheet.confirm.cancel")}
        </Button>
        <Button variant="primary" onClick={onConfirm} disabled={isSubmitting}>
          {isSubmitting
            ? t("personalSheet.confirm.submitting")
            : isChiefDirectEdit
              ? t("personalSheet.confirm.chiefSubmit")
              : t("personalSheet.confirm.submit")}
        </Button>
      </div>
    );
  }

  const fileEntries = Object.entries(files);
  const sectionEntries = Object.entries(changes);
  const hasContent = sectionEntries.length > 0 || fileEntries.length > 0;

  return (
    <ModalForm icon={Edit2} onClose={onClose} resize width="w-3xl" footer={renderFooter()}>
      <div className="flex flex-col gap-6">
        <h2 className="text-heading-md content-base-primary">
          {isChiefDirectEdit ? t("personalSheet.confirm.chiefTitle") : t("personalSheet.confirm.title")}
        </h2>

        {!hasContent && (
          <p className="text-body-regular-md content-base-secondary">{t("personalSheet.confirm.noChanges")}</p>
        )}

        {sectionEntries.map(([sectionName, sectionValue]) => {
          const sectionLabelKey = SECTION_LABEL_MAP[sectionName];
          const sectionLabel = sectionLabelKey ? t(sectionLabelKey) : sectionName;
          const isArray = Array.isArray(sectionValue);

          return (
            <div key={sectionName} className="flex flex-col gap-3">
              <p className="text-body-bold-md content-base-primary">{sectionLabel}</p>
              {isArray
                ? renderArraySection(sectionName, sectionValue as unknown[])
                : renderObjectSection(sectionName, sectionValue as Record<string, unknown>)}
            </div>
          );
        })}

        {fileEntries.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-body-bold-md content-base-primary">{t("personalSheet.confirm.attachedFiles")}</p>
            {fileEntries.map(([key, file]) => {
              const fieldName = key.split(".").pop() ?? key;
              const labelKey = FILE_LABEL_MAP[fieldName];
              return (
                <FileViewer key={key} file={file} customFileName={labelKey ? t(labelKey) : fieldName} isDownloadable />
              );
            })}
          </div>
        )}
      </div>
    </ModalForm>
  );
}
